import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Briefcase, Sparkles } from "lucide-react";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import { createArticleSchema } from "@/utils/schemaHelpers";

const HoroscopoPiscis = () => {
  const articleSchema = createArticleSchema(
    "Piscis: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Piscis. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 19 de febrero y el 20 de marzo.",
    `${window.location.origin}/horoscopo-piscis`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Piscis",
        "item": `${window.location.origin}/horoscopo-piscis`
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Piscis: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Piscis. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 19 de febrero y el 20 de marzo." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-piscis`} />
        <meta property="og:title" content="Piscis: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Piscis. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-piscis`} />
        <meta property="og:type" content="article" />
      </Helmet>
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <div className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♓</div>
              <h1 className="text-5xl font-bold mb-4 text-glow">
                Piscis: Horóscopo Diario, Características y Compatibilidad con los 12 Signos
              </h1>
              <p className="text-xl text-muted-foreground">19 de Febrero - 20 de Marzo</p>
            </div>

            <section className="mb-16">
              <h2 className="text-3xl font-bold mb-6 text-primary">Tu Horóscopo Piscis: Predicciones Actualizadas</h2>
              
              <Card className="p-8 gradient-celestial">
                <Tabs defaultValue="hoy" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-8">
                    <TabsTrigger value="hoy">Hoy</TabsTrigger>
                    <TabsTrigger value="semanal">Semanal</TabsTrigger>
                    <TabsTrigger value="mensual">Mensual</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="hoy" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Heart className="w-6 h-6 text-primary" />
                        Horóscopo de Hoy para Piscis
                      </h3>
                      <p className="text-center text-sm text-muted-foreground mb-6">Número: 7 | Color: Aqua</p>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-primary">💖 Amor</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            La sensibilidad e imaginación tiñen tu vida amorosa. Es un día para la conexión espiritual y la empatía profunda. Sueña despierto/a con tu pareja.
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-secondary">💼 Trabajo</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Tu creatividad e intuición son tus mejores guías. Las artes o cualquier trabajo que requiera sensibilidad están muy favorecidos. Confía en tus corazonadas.
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-accent">✨ Salud</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Busca la paz interior a través de la meditación, la música o el arte. Protege tus pies y busca momentos de soledad reparadora para recargar tus energías.
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-accent">💰 Dinero</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Tu intuición es tu mejor guía financiera hoy, especialmente en áreas creativas. Ten cuidado de no ser demasiado generoso/a y establece límites claros en tus gastos.
                          </p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="semanal" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Briefcase className="w-6 h-6 text-secondary" />
                        Horóscopo Semanal: Tu Guía para los Próximos 7 Días
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Una semana de profunda conexión emocional y creatividad desbordante. Los primeros días favorecen la introspección y el trabajo artístico. Tu sensibilidad será tu guía más confiable en las decisiones importantes.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        El fin de semana invita a compartir tus visiones y sueños con personas cercanas. Tu compasión natural atraerá a quienes necesitan tu comprensión. Es momento de confiar en tu intuición mística.
                      </p>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="mensual" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-accent" />
                        Horóscopo Mensual Piscis: Enfoque y Oportunidades Clave
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Este mes marca un período de profundización espiritual y expresión creativa. La primera mitad favorece proyectos artísticos, sanación emocional y la conexión con tu mundo interior. Tu empatía será tu mayor don.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        La segunda mitad invita a materializar tus sueños en la realidad práctica. Es momento de combinar tu visión mística con pasos concretos. Tu capacidad de soñar puede inspirar transformaciones hermosas en tu vida y la de otros.
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </Card>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default HoroscopoPiscis;